import { useState, useCallback, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import api from "services/api";

import getQueryParams from "common/get-query-params";
import encodeQueryParams from "common/encode-query-params";
import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import CONSTANTS from "constants/constant";

import ActionEnum from "enum/action";

import ErrorResponseDto from "dto/services/error-response-dto";
import PageFaqDto, { PageFaqTableDto, PageFaqTableCreatePayloadDto, PageFaqTableQueryParamsDto } from "dto/pages/faq/page-faq-dto";

import Button from "components/app-button";
import AppHeading from "components/app-heading";
import FaqTable from "components/pages/page-faq/app-faq-table";

const PageFaq = () => {
	const [disableAddNew, setDisableAddNew] = useState(false);
	const [newRow, setNewRow] = useState<PageFaqTableCreatePayloadDto>();
	const [faqData, setFaqData] = useState<PageFaqDto>();
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const [isFetching, setIsFetching] = useState(false);
	const queryParams = useRef(getQueryParams<PageFaqTableQueryParamsDto>(searchParams, { page: 0 }));

	const onHandleGetFaq = useCallback(async () => {
		setIsFetching(true);

		try {
			const { page } = queryParams.current;
			const payload = { size: 10, page };
			const response = await api.get.faq.faqs(payload);

			setFaqData(response?.data?.data?.list);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		} finally {
			setIsFetching(false);
		}
	}, []);

	//prettier-ignore
	const onUpdateFaqs = useCallback(async (payload: PageFaqTableDto, page? : number) => {
		let response = null;

		try {
			await api.put.faq.update({ id: payload.id }, payload);

			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}

		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);

			if (typeof page === "number") {
				queryParams.current.page = page;
			}

			onHandleGetFaq();
		}
	}, [onHandleGetFaq]);

	//prettier-ignore
	const onHandleAddFaq = useCallback(async (payload: PageFaqTableCreatePayloadDto) => {
		let response = null;

		try {
			await api.post.faq.create(payload);

			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}

		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.CREATED, ActionEnum.Create);

			if (faqData && faqData?.numberOfElements === faqData.size) {
				queryParams.current.page = faqData.totalElements % faqData.size === 0 ? faqData.totalPages : faqData.totalPages - 1;
			}

			onHandleGetFaq();
		}
	}, [faqData, onHandleGetFaq]);

	const onHandlePageChange = (page: number) => {
		queryParams.current.page = page;

		memoSetSearchParams.current(encodeQueryParams(queryParams.current));

		onHandleGetFaq();
	};

	const onHandleAddNew = () => {
		setDisableAddNew(true);
		setNewRow({ question: "", answer: "", status: "active" });
	};

	useEffect(() => {
		onHandleGetFaq();
	}, [onHandleGetFaq]);

	return (
		<div className="page-faq">
			<div className="faq">
				<AppHeading title="FAQ">
					<AppHeading.Divider />
				</AppHeading>

				<div className="faq__button">
					<Button label="Add New" disabled={disableAddNew || (faqData?.totalElements || 0) >= 10} onClick={onHandleAddNew} />
				</div>

				{/* prettier-ignore */}
				<FaqTable faqs={faqData?.content || []} setDisableAddNew={setDisableAddNew} newRow={newRow} setNewRow={setNewRow} onUpdateFaqs={onUpdateFaqs} onCreateFaqs={onHandleAddFaq} onHandlePageChange={onHandlePageChange} fetching={isFetching} paginationTable={{currentPage: faqData?.number || 0, totalPages: faqData?.totalPages || 0, totalElements: faqData?.totalElements || 0, numberOfElements: faqData?.numberOfElements || 0, tableSize: faqData?.size || 10 }} />
			</div>
		</div>
	);
};

export default PageFaq;
